import { enGB, sv } from "date-fns/locale";
import React, { useEffect, useRef, useState } from "react";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { getFontSize, TextSizeType } from "../../../utils/theme.utils";
import AppIcon from "../AppIcon/AppIcon";
import AppText, { FormLabel } from "../AppText/AppText";

registerLocale("en", enGB);
registerLocale("sv", sv);
setDefaultLocale("en");

interface AppDatePickerProps extends AppDatePickerStyleProps {
	onChange?: (date: Date) => void;
	selectedDate?: Date;
	label?: string;
	excludeDates?: Date[];
	minDate?: any;
	placeholderText?: string;
	showTimeSelect?: boolean;
	description?: string;
	/** Forwarded to the input's `name` attribute (forms and browser tests). */
	name?: string;
}

interface AppDatePickerStyleProps {
	inputStyles?: boolean;
	inputWidth?: string;
	disabled?: boolean;
	margin?: string;
	fontSize?: TextSizeType;
	showTimeSelect?: boolean;
	mandatory?: boolean;
	tooltip?: string;
}

const AppDatePicker = React.forwardRef<HTMLDivElement, AppDatePickerProps>(
	(
		{
			onChange,
			selectedDate,
			inputStyles,
			label,
			tooltip,
			excludeDates,
			inputWidth,
			minDate,
			disabled,
			margin,
			fontSize,
			placeholderText,
			showTimeSelect,
			description,
			mandatory,
			name,
		},
		ref
	) => {
		const [selectedDateValue, setSelectedDateValue] = useState<Date | undefined>(selectedDate);

		const { t } = useTranslation();

		const datePickerRef = useRef<DatePicker>(null);

		const handleOnChange = (date: Date) => {
			setSelectedDateValue(date);
			onChange && onChange(date);
		};

		const handleDatePickerIconClick = () => {
			if (datePickerRef.current) datePickerRef.current.setOpen(true);
		};

		useEffect(() => {
			setSelectedDateValue(selectedDate);
		}, [selectedDate]);

		const datePickerComponent = (
			<DatePicker
				name={name}
				disabled={disabled}
				minDate={minDate}
				selected={selectedDateValue}
				onChange={(date: Date) => handleOnChange(date)}
				className="date-picker-styles"
				dateFormat={showTimeSelect ? "yyyy-MM-dd HH:mm" : "yyyy-MM-dd"}
				excludeDates={excludeDates}
				placeholderText={placeholderText || ""}
				showTimeSelect={showTimeSelect || false}
				timeFormat="HH:mm"
				timeIntervals={1}
				ref={datePickerRef}
				locale={"sv"}
			/>
		);

		return (
			<DatePickerStyles
				ref={ref}
				disabled={disabled}
				inputWidth={inputWidth}
				inputStyles={inputStyles}
				margin={margin}
				fontSize={fontSize}
			>
				{label && (
					<FormLabel tooltip={tooltip}>
						{label && t(label as any)}
						{mandatory ? " *" : ""}
					</FormLabel>
				)}

				{inputStyles ? (
					<PickerWrapper disabled={disabled}>
						<AppIcon icon="calendar" onClick={handleDatePickerIconClick} size="14px" />
						<DatePickerStyled showTimeSelect={showTimeSelect}>
							{datePickerComponent}
						</DatePickerStyled>
					</PickerWrapper>
				) : (
					<DatePickerStyled showTimeSelect={showTimeSelect}>{datePickerComponent}</DatePickerStyled>
				)}

				{description && (
					<AppText margin="0.35rem 0 0 0.15rem" size="xs" color="grayText">
						{t(description as any)}
					</AppText>
				)}
			</DatePickerStyles>
		);
	}
);

AppDatePicker.displayName = "AppDatePicker";

export default AppDatePicker;

const DatePickerStyles = styled.div<AppDatePickerStyleProps>`
	position: relative;
	width: ${(props) => (props.inputWidth ? props.inputWidth : "100%")};
	margin: ${(props) => props?.margin};

	.date-picker-styles {
		border: none;
		background: transparent;
		font-size: ${(props) =>
			props.fontSize ? getFontSize(props.fontSize) : props.theme.fontSizes.FONT_SIZE_M};
		color: ${(props) =>
			props.inputStyles ? props.theme.colors.COLOR_BLACK : props.theme.colors.COLOR_ACTION_BLUE};
		margin-left: ${(props) => (props.inputStyles ? "0.5rem" : 0)};
		text-decoration: ${(props) => (props.inputStyles ? null : "underline")};
		opacity: ${(props) => (props.disabled ? "0.5" : "1")};

		&::-webkit-input-placeholder {
			color: ${(props) =>
				props.inputStyles
					? props.theme.colors.COLOR_GRAY_TEXT
					: props.theme.colors.COLOR_ACTION_BLUE};
		}
	}
`;

const PickerWrapper = styled.div<AppDatePickerStyleProps>`
	background-color: #fff;
	padding: 0.5rem;
	border: 1px solid #dbdbdb;
	border-radius: 2px;
	display: flex;

	& > svg {
		&:hover {
			cursor: ${(props) => (props.disabled ? "default" : "pointer")};
		}
	}
`;

const DatePickerStyled = styled.div<AppDatePickerStyleProps>`
	.react-datepicker-popper {
		left: ${(props) => (props.showTimeSelect ? "auto !important" : "0")};
		right: ${(props) => (props.showTimeSelect ? "0 !important" : "auto")};
		min-width: ${(props) => (props.showTimeSelect ? "340px" : "auto")};
	}
`;
